import { TerciarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import BookingHistory from 'features/BookingDetails/BookingHistory'
import ReservationCompareDialog from 'features/MonthlyOTAConversions/OTABookingsList/ReservationCompareDialog'
import SelectBarChart from 'features/MonthlyOTAConversions/OTABookingsList/SelectBarChart'
import moment from 'moment'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { formatPrice } from 'utils/common'

import styles from './OTABookingsList.module.scss'
import { getDateRange, getHotelDisplayName, sortResults } from './utils/helpers'
import { useOTABookings } from './utils/useOTABookings'

const OTABookingsList = ({
  month,
  year,
  kind,
  parentChain,
  cityToFilter,
  groupToFilter,
  stateToFilter,
  isOtaOnly
}) => {
  const [sortIndex, setSortIndex] = useState(2)
  const [sortAscending, setSortAscending] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeBookingId, setActiveBookingId] = useState()
  const [activePriorBookingId, setActivePriorBookingId] = useState()
  const [isCompareDialogVisible, setIsCompareDialogVisible] = useState(false)
  const [activeSubscriptionId, setActiveSubscriptionId] = useState('')
  const [isBookingHistoryVisible, setIsBookingHistoryVisible] = useState(false)

  const isDemoMode = useSelector(s => s.login.isDemoMode)

  const { isLoading, isErred, hotels } = useOTABookings({
    month,
    year,
    kind,
    parentChain,
    cityToFilter,
    groupToFilter,
    stateToFilter,
    isOtaOnly
  })

  function handleTitleClick(i) {
    const sortableIndexes = [0, 1, 2, 3, 4, 5, 6, 7]

    if (sortIndex === i) {
      setSortAscending(d => !d)
    }

    if (sortableIndexes.includes(i)) {
      setSortIndex(i)
    }
  }

  if (isLoading) {
    return (
      <div className={styles.bookingsWrapper}>
        <div className={styles.bookingsHeader}>
          <h2 className={styles.bookingsTitle}>
            Bookings for {month}, {year}
          </h2>{' '}
        </div>
        <LoadingIndicator inline />
      </div>
    )
  }

  if (isErred) {
    return (
      <div className={styles.bookingsWrapper}>
        <div className={styles.bookingsHeader}>
          <h2 className={styles.bookingsTitle}>
            Bookings for {month}, {year}
          </h2>{' '}
        </div>
        <p>Something went wrong while getting the booking list</p>
      </div>
    )
  }

  return (
    <div className={styles.bookingsWrapper}>
      {isCompareDialogVisible && (
        <ReservationCompareDialog
          bookingId={activeBookingId}
          priorBookingId={activePriorBookingId}
          onClose={() => setIsCompareDialogVisible(false)}
          priorBookingTitle="OTA booking"
          bookingTitle="Direct booking"
        />
      )}
      {isBookingHistoryVisible && activeSubscriptionId && (
        <BookingHistory
          subscriberId={activeSubscriptionId}
          onClose={() => {
            setIsBookingHistoryVisible(false)
            setActiveSubscriptionId(null)
          }}
        />
      )}
      <div className={styles.bookingsHeader}>
        {month ? (
          <h2 className={styles.bookingsTitle}>
            Bookings for {month}, {year}
          </h2>
        ) : (
          <h2 className={styles.bookingsTitle}>Bookings list</h2>
        )}
        <input
          type="search"
          className={styles.searchInput}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search with guest name"
          disabled={!month}
        />
      </div>
      {month && hotels ? (
        <div className={styles.tableHolder}>
          <Table
            widths={[170, 120, 110, 110, 110, 110, 110, 100, 120]}
            sortIndex={sortIndex}
            sortDirection={sortAscending ? 'up' : 'down'}
            handleTitleCellClick={handleTitleClick}
          >
            {hotels.map((hotel, hotelIndex) => (
              <div key={hotel.subsidiaryId}>
                <p className={styles.hotelName}>
                  {getHotelDisplayName(hotel.hotelName, hotelIndex, isDemoMode)}
                </p>
                <TitleRow className={styles.titleRow}>
                  <TitleCell>Guest name</TitleCell>
                  <TitleCell>OTA name</TitleCell>
                  <TitleCell>OTA booking</TitleCell>
                  <TitleCell>OTA stay dates</TitleCell>
                  <TitleCell>Invite date</TitleCell>
                  <TitleCell>Direct booking</TitleCell>
                  <TitleCell>Stay dates</TitleCell>
                  <TitleCell>Total rate</TitleCell>
                  <TitleCell></TitleCell>
                </TitleRow>
                {sortResults(hotel.bookings, sortIndex, sortAscending)
                  .filter(b =>
                    b?.fullName
                      ? b?.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
                      : true
                  )
                  .map(b => (
                    <Row className={styles.row}>
                      <Cell className={styles.fullNameRow}>
                        {b.subscriptionId ? (
                          <button
                            className={styles.fullNameButton}
                            onClick={() => {
                              setActiveSubscriptionId(b.subscriptionId)
                              setIsBookingHistoryVisible(true)
                            }}
                          >
                            {b.fullName}
                          </button>
                        ) : (
                          b.fullName
                        )}
                      </Cell>
                      <Cell>
                        <span
                          title={b.otaBooking?.agencyName}
                          style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            maxWidth: '100px'
                          }}
                        >
                          {b.otaBooking?.agencyName}
                        </span>
                      </Cell>
                      <Cell>
                        {b.otaBooking?.bookingDate
                          ? moment(b.otaBooking?.bookingDate).format('MMM D, YYYY')
                          : '-'}
                      </Cell>
                      <Cell>
                        <span
                          title={`${moment(b.otaBooking?.checkIn).format(
                            'MMM D, YYYY'
                          )} - ${moment(b.otaBooking?.checkOut).format('MMM D, YYYY')}`}
                        >
                          {getDateRange(b.otaBooking?.checkIn, b.otaBooking?.checkOut)}
                        </span>
                      </Cell>
                      <Cell>
                        {moment(b.directBooking?.textedOn).format('MMM D, YYYY')}
                      </Cell>
                      <Cell>
                        {b.directBooking?.bookingDate
                          ? moment(b.directBooking?.bookingDate).format('MMM D, YYYY')
                          : '-'}
                      </Cell>
                      <Cell>
                        <span
                          title={`${moment(b.directBooking?.checkIn).format(
                            'MMM D, YYYY'
                          )} - ${moment(b.directBooking?.checkOut).format(
                            'MMM D, YYYY'
                          )}`}
                        >
                          {getDateRange(
                            b.directBooking?.checkIn,
                            b.directBooking?.checkOut
                          )}
                        </span>
                      </Cell>
                      <Cell>{formatPrice(b.directBooking?.totalRate)}</Cell>
                      <Cell>
                        <TerciarySmallButton
                          disabled={!(b.directBooking?.id && b.otaBooking?.id)}
                          onClick={() => {
                            setActiveBookingId(b.directBooking?.id)
                            setActivePriorBookingId(b.otaBooking?.id)
                            setIsCompareDialogVisible(true)
                          }}
                        >
                          Compare
                        </TerciarySmallButton>
                      </Cell>
                    </Row>
                  ))}
              </div>
            ))}
          </Table>
        </div>
      ) : (
        <div className={styles.selectMonthIconHolder}>
          <SelectBarChart className={styles.selectMonthIcon} />
          <p className={styles.selectMonthText}>
            Select a month from charts to see the bookings
          </p>
        </div>
      )}
    </div>
  )
}

export default OTABookingsList
